import {
  ActionActor,
  BatchMode,
  ChannelType,
  ISupportingActor,
  PARTICIPANT_ACTOR_KEY,
  SupportingActorType
} from '@dadohr/shared/domainTypes/experience';
import { Integration } from '@dadohr/shared/domainTypes/integrations';
import {
  ScheduleChannel,
  ScheduleChannelType
} from '@dadohr/shared/domainTypes/schedules';
import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { compact, sortBy, uniqBy } from 'lodash';
import {
  MailPlus,
  Plus,
  RotateCcw,
  SlidersHorizontal,
  User,
  UserPlus
} from 'lucide-react';
import * as UX from '../../newux/kit';
import { ACTOR_COLORS_LIGHT, useActorDescriptorsFromTemplate } from '../../../Actors';
import { ChannelIcon } from '../../../Channels';
import { ClassicBlue600 } from './classicBlue600';
import React, { useMemo, useState } from 'react';
import { AutocompleteMultiFreeSolo } from '../../../../../../components/Autocomplete';
import { Label } from '../../../../../../components/CustomInputLabel';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { TokenDefinition } from '../../../../../../components/MarkdownText';
import { RadioPanel } from '../../../../../../components/RadioPanel';
import { TextFieldWithReplacementTokens } from '../../../../../../components/TextFieldWithReplacementTokens';
import {
  UnsafeTFunction,
  useUnsafeTranslation
} from '../../../../../../i18next';
import { FlexContainer } from '../../../../../../layout/Flex';
import {
  channelToKey,
  channelToString
} from '../../../../../../services/channels';
import { useFeatureEnabled } from '../../../../../../services/features';
import { useIntegrationsContext } from '../../../../../../services/integrations/context';
import {
  ActionActorSelectorMulti,
  actionActorToString
} from '../../AutomationBuilder/shared';
import { getSelectableSupportingActors } from '../../../../../../services/experiences/actorUsage';
import { TaskBuilderContext } from '../types';
import { useEmailTokens, useNameTokens } from './BlockSettings/common';
import { SettingsSection, SideContainer } from './layout';
import { SideContainerHeader } from './SideContainerHeader';
import { TaskSettingsOverviewSection } from './TaskSettingsOverviewSection';

const SHOW_PER_COHORT_BATCHING = true;

const useActorOptions = (
  supportingActors: {
    [key: string]: ISupportingActor;
  },
  type?: 'TASK' | 'MSG'
) => {
  const { t } = useUnsafeTranslation();
  return useMemo(
    () => [
      {
        value: PARTICIPANT_ACTOR_KEY,
        label: t('Participant'),
        isSlackChannel: false,
        disabled: false,
        tooltip: undefined as string | undefined
      },
      ...getSelectableSupportingActors(supportingActors).map((a) => ({
        value: a.key,
        label: a.name,
        isSlackChannel: a.type === SupportingActorType.CHANNEL,
        disabled: a.type === SupportingActorType.CHANNEL && type === 'TASK',
        tooltip:
          a.type === SupportingActorType.CHANNEL && type === 'TASK'
            ? 'Tasks cannot be assigned to Slack channel supporting actors'
            : undefined
      }))
    ],
    [supportingActors, t]
  );
};

const ICON = User;

const AssignmentGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  gridColumnGap: p.theme.spacing(1)
}));

export const TaskSettingsOverviewSectionAssignment = ({
  title,
  assigneeNoun,
  actor,
  channels,
  customSender,
  receivesCopy,
  supportingActors,
  batchMode,
  onEdit,
  divider
}: {
  title: string;
  assigneeNoun: string;
  actor: string;
  channels: ScheduleChannel[];
  customSender?: {
    name: string;
    replyTo: string;
  } | null;
  receivesCopy?: {
    roles: { key: string; emailField: string }[];
    emails: string[];
  };
  supportingActors: { [key: string]: ISupportingActor };
  batchMode: BatchMode;
  onEdit?: () => void;
  divider?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const actorOptions = useActorOptions(supportingActors);
  const selectedOption = useMemo(
    () => actorOptions.find((a) => a.value === actor) || actorOptions[0],
    [actor, actorOptions]
  );
  const ccLabels = useMemo(() => {
    if (!receivesCopy) return [];
    const roleLabels = (receivesCopy.roles as any[]).map((r) =>
      actionActorToString(
        { type: 'actor', key: typeof r === 'string' ? r : r.key, emailField: typeof r === 'string' ? 'email' : r.emailField, optional: false },
        supportingActors,
        t
      )
    );
    return [...roleLabels, ...receivesCopy.emails];
  }, [receivesCopy, supportingActors, t]);

  return (
    <TaskSettingsOverviewSection
      icon={ICON}
      title={title}
      onEdit={onEdit}
      divider={divider}
    >
      {customSender?.name || customSender?.replyTo ? (
        <AssignmentGrid>
          <div>{assigneeNoun}:</div>
          <div>
            {selectedOption.label}{' '}
            <Typography variant="caption" component="span">
              ({compact(channels.map((c) => channelToString(c, t))).join(', ')})
            </Typography>
          </div>
          {customSender.name && (
            <>
              <div>{t('Sender')}:</div>
              <div>{customSender.name}</div>
            </>
          )}
          {customSender.replyTo && (
            <>
              <div>{t('Reply to')}:</div>
              <div>{customSender.replyTo}</div>
            </>
          )}
          {ccLabels.length > 0 && (
            <>
              <Typography variant="caption" component="div" style={{ marginTop: 8 }}>{t('Copies sent to')}:</Typography>
              <Typography variant="caption" component="div">{ccLabels.join(', ')}</Typography>
            </>
          )}
        </AssignmentGrid>
      ) : (
        <>
          <div>{selectedOption.label}</div>{' '}
          <Typography variant="caption" component="span">
            ({compact(channels.map((c) => channelToString(c, t))).join(', ')})
          </Typography>
          {ccLabels.length > 0 && (
            <Typography variant="caption" component="div" style={{ marginTop: 8 }}>
              {t('Copies sent to')}: {ccLabels.join(', ')}
            </Typography>
          )}
        </>
      )}

      {actor !== PARTICIPANT_ACTOR_KEY &&
        batchMode === BatchMode.PER_COHORT && (
          <FlexContainer marginTop={1}>
            {t('Send once per cohort')}
          </FlexContainer>
        )}
    </TaskSettingsOverviewSection>
  );
};

const CHANNELS: ScheduleChannel[] = [
  { type: ScheduleChannelType.EMAIL, emailFrom: 'email' },
  { type: ScheduleChannelType.EMAIL, emailFrom: 'personalEmail' }
];

const getSelectableChannels = (
  actor: string,
  activeIntegrations: Set<Integration>,
  smsEnabled: boolean,
  batchMode: BatchMode,
  isSlackChannel: boolean,
  t: UnsafeTFunction
): {
  channel: ScheduleChannel;
  disabled?: boolean;
  tooltip?: React.ReactNode;
}[] => {
  if (isSlackChannel) {
    //return [];
    return [{ channel: { type: ScheduleChannelType.SLACK }, disabled: true }];
  }
  let selectableChannels: {
    channel: ScheduleChannel;
    disabled?: boolean;
    tooltip?: React.ReactNode;
  }[] = (actor === PARTICIPANT_ACTOR_KEY
    ? CHANNELS
    : CHANNELS.filter(
        (c) =>
          c.type !== ScheduleChannelType.EMAIL ||
          c.emailFrom !== 'personalEmail'
      )
  ).map((channel) => ({ channel }));

  if (activeIntegrations.has(Integration.SLACK)) {
    selectableChannels = [
      ...selectableChannels,
      { channel: { type: ScheduleChannelType.SLACK } }
    ];
  }

  if (activeIntegrations.has(Integration.MICROSOFT)) {
    selectableChannels = [
      ...selectableChannels,
      { channel: { type: ScheduleChannelType.MS_TEAMS } }
    ];
  }

  if (smsEnabled) {
    const disabled = batchMode !== BatchMode.DEFAULT;
    selectableChannels = [
      ...selectableChannels,
      {
        channel: { type: ScheduleChannelType.SMS },
        disabled,
        tooltip: disabled
          ? t('This channel cannot be used for Per Cohort notifications')
          : undefined
      }
    ];
  }

  return selectableChannels;
};

const CustomSenderTextField = ({
  label,
  value,
  defaultValue,
  onChange,
  tokens,
  newUx = false
}: {
  label: React.ReactNode;
  value: string;
  defaultValue: string;
  onChange: (nextValue: string) => void;
  tokens: TokenDefinition[];
  newUx?: boolean;
}) => {
  // NEW_UX: light compact input (blue "+" variable popover), matching the Content
  // panel fields; classic keeps the outlined field + reset adornment.
  if (newUx) {
    return (
      <>
        <UX.FieldLabel>{label}</UX.FieldLabel>
        <TextFieldWithReplacementTokens
          fullWidth
          variant="outlined"
          value={value || defaultValue || ''}
          onChange={(val) => onChange(val === defaultValue ? '' : val)}
          tokens={tokens}
          compact
        />
      </>
    );
  }
  return (
    <TextFieldWithReplacementTokens
      label={label}
      fullWidth
      value={value || defaultValue || ''}
      onChange={(val) => onChange(val === defaultValue ? '' : val)}
      InputProps={{
        endAdornment: value && value !== defaultValue && (
          <InputAdornment position="end">
            <IconButton
              onClick={() => {
                onChange('');
              }}
            >
              <RotateCcw size={12} />
            </IconButton>
          </InputAdornment>
        )
      }}
      tokens={tokens}
    />
  );
};

export const TaskSettingsAssignment = <
  T extends {
    actor: string;
    channels: ScheduleChannel[];
    customSender?: {
      name: string;
      replyTo: string;
    } | null;
    receivesCopy?: {
      roles: { key: string; emailField: string }[];
      emails: string[];
    };
    batchMode: BatchMode;
  }
>({
  title,
  description,
  onBack,
  value,
  onChange,
  expTpl,
  type,
  tokens,
  newUx = false,
  newUxSection = 'delivery'
}: {
  title: string;
  description: string;
  onBack: () => void;

  value: T;
  onChange: React.Dispatch<React.SetStateAction<T>>;
  expTpl: TaskBuilderContext;
  type: 'TASK' | 'MSG';
  tokens: TokenDefinition[];
  // NEW_UX: render with the kit design (chips/radio-cards) reusing all the same logic.
  newUx?: boolean;
  // Which half of the prototype's split this instance renders: 'delivery' =
  // Channels + Send frequency + Sender; 'audience' = Recipient + CC. Same editor,
  // same handlers, rendered as two sections across two panels.
  newUxSection?: 'delivery' | 'audience';
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const { actor, channels, batchMode } = value;
  const { active: activeIntegrations } = useIntegrationsContext();
  const smsEnabled = useFeatureEnabled('SMS_V1');
  const actorOptions = useActorOptions(expTpl.supportingActors, type);
  const actorDescriptors = useActorDescriptorsFromTemplate(expTpl.supportingActors);

  const {
    onChangeChannels,
    onChangeActor,
    onChangeCustomSenderName,
    onChangeCustomSenderReplyTo,
    onBatchModeChange,
    onChangeReceivesCopyRoles,
    onChangeReceivesCopyEmails
  } = useMemo(() => {
    return {
      onChangeChannels: (nextChannels: ScheduleChannel[]) =>
        onChange((v) => ({
          ...v,
          channels: nextChannels
        })),
      onChangeActor: (nextActor: string) =>
        onChange((v) => ({
          ...v,
          actor: nextActor,
          batchMode:
            nextActor === PARTICIPANT_ACTOR_KEY
              ? BatchMode.DEFAULT
              : v.batchMode
        })),
      onChangeCustomSenderName: (nextName: string) =>
        onChange((v) => ({
          ...v,
          customSender: {
            replyTo: '',
            ...v.customSender,
            name: nextName
          }
        })),

      onChangeCustomSenderReplyTo: (nextReplyTo: string) =>
        onChange((v) => ({
          ...v,
          customSender: {
            name: '',
            ...v.customSender,
            replyTo: nextReplyTo
          }
        })),
      onBatchModeChange: (nextBatchMode: BatchMode) =>
        onChange((v) => ({
          ...v,
          batchMode: nextBatchMode
        })),
      onChangeReceivesCopyRoles: (nextRoles: { key: string; emailField: string }[]) =>
        onChange((v) => ({
          ...v,
          receivesCopy: {
            emails: v.receivesCopy?.emails || [],
            roles: nextRoles
          }
        })),
      onChangeReceivesCopyEmails: (nextEmails: string[]) =>
        onChange((v) => ({
          ...v,
          receivesCopy: {
            roles: v.receivesCopy?.roles || [],
            emails: nextEmails
          }
        }))
    };
  }, [onChange]);

  const emailTokens = useEmailTokens(tokens);
  const nameTokens = useNameTokens(tokens);

  const hasSMSChannel = !!channels.find(
    (c) => c.type === ScheduleChannelType.SMS
  );

  const selectedActorOptionIsSlackChannel = !!actorOptions.find(
    (o) => o.value === actor
  )?.isSlackChannel;

  const selectedCCRoles = useMemo(
    () => (value.receivesCopy?.roles || []).map((r: any) => ({
      type: 'actor' as const,
      key: typeof r === 'string' ? r : r.key,
      emailField: typeof r === 'string' ? 'email' : r.emailField,
      optional: false
    })),
    [value.receivesCopy]
  );
  const selectedCCEmails = useMemo(
    () => value.receivesCopy?.emails || [],
    [value.receivesCopy]
  );

  // NEW_UX CC: "CC a role" / "CC a specific person" open an inline input (prototype).
  const [ccMode, setCcMode] = useState<null | 'role' | 'person'>(null);
  const [ccRoleDraft, setCcRoleDraft] = useState('');
  const [ccEmailDraft, setCcEmailDraft] = useState('');

  if (newUx) {
    const selectableChannels = getSelectableChannels(
      actor,
      activeIntegrations,
      smsEnabled,
      batchMode,
      selectedActorOptionIsSlackChannel,
      t
    );
    const channelKeys = new Set(channels.map(channelToKey));
    const toggleChannel = (c: { channel: ScheduleChannel; disabled?: boolean }) => {
      if (c.disabled) return;
      const key = channelToKey(c.channel);
      if (channelKeys.has(key)) {
        const i = channels.findIndex((ch) => channelToKey(ch) === key);
        if (i !== -1) {
          const next = channels.slice();
          next.splice(i, 1);
          onChangeChannels(next);
        }
      } else {
        onChangeChannels(
          uniqBy(sortBy([...channels, c.channel], channelToKey), channelToKey)
        );
      }
    };
    const channelIcon = (c: ScheduleChannel) => <ChannelIcon channel={c} size={16} />;
    const selectActor = (nextActor: string) => {
      const supportingActor = expTpl.supportingActors[nextActor] as
        | ISupportingActor
        | undefined;
      const slackChannel: ScheduleChannel = { type: ScheduleChannelType.SLACK };
      const nextChannels =
        supportingActor?.type === SupportingActorType.CHANNEL &&
        supportingActor?.channeltype === ChannelType.SLACK
          ? [slackChannel]
          : CHANNELS.filter(
              (c) =>
                c.type === ScheduleChannelType.EMAIL && c.emailFrom === 'email'
            );
      onChangeChannels(nextChannels);
      onChangeActor(nextActor);
    };
    const recipientSection = (
      <>
        <UX.SectionLabel style={{ marginTop: 0 }}>
          {type === 'TASK' ? t('Assignee') : t('Recipient')}
        </UX.SectionLabel>
        <ClassicBlue600>
          <TextField
            select
            variant="outlined"
            fullWidth
            value={actor}
            onChange={(e) => selectActor(e.target.value)}
          >
            {actorOptions
              .filter((o) => !o.disabled)
              .map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
          </TextField>
        </ClassicBlue600>
      </>
    );

    // CC helpers — reuse the real receivesCopy handlers.
    const ccRolesForChange = selectedCCRoles.map((s) => ({
      key: s.key,
      emailField: s.emailField
    }));
    const ccRoleLabel = (r: { key: string; emailField: string }) =>
      actionActorToString(
        { type: 'actor', key: r.key, emailField: r.emailField, optional: false },
        expTpl.supportingActors,
        t
      );
    const availableCCRoles: { key: string; emailField: string }[] = [
      ...(actor !== PARTICIPANT_ACTOR_KEY
        ? (['email', 'personalEmail'] as const)
            .filter(
              (emailField) =>
                !selectedCCRoles.some(
                  (s) =>
                    s.key === PARTICIPANT_ACTOR_KEY && s.emailField === emailField
                )
            )
            .map((emailField) => ({ key: PARTICIPANT_ACTOR_KEY, emailField }))
        : []),
      ...getSelectableSupportingActors(expTpl.supportingActors, {
        includeChannelActors: false
      })
        .filter((a) => a.key !== actor && a.key !== PARTICIPANT_ACTOR_KEY)
        .filter(
          (a) =>
            !selectedCCRoles.some(
              (s) => s.key === a.key && s.emailField === 'email'
            )
        )
        .map((a) => ({ key: a.key, emailField: 'email' }))
    ];
    const ccRoleOptionValue = (r: { key: string; emailField: string }) =>
      `${r.key}::${r.emailField}`;
    const hasSupportingActors =
      getSelectableSupportingActors(expTpl.supportingActors, {
        includeChannelActors: false
      }).length > 0;
    const addCcRole = () => {
      if (!ccRoleDraft) return;
      const [key, emailField] = ccRoleDraft.split('::');
      onChangeReceivesCopyRoles([...ccRolesForChange, { key, emailField }]);
      setCcRoleDraft('');
      setCcMode(null);
    };
    const addCcEmail = () => {
      const e = ccEmailDraft.trim();
      if (!e) return;
      onChangeReceivesCopyEmails([...selectedCCEmails, e]);
      setCcEmailDraft('');
      setCcMode(null);
    };
    const ccChipRowStyle: React.CSSProperties = {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
      marginBottom: 8
    };
    const ccSection = (
      <>
        <UX.SectionLabel>{t('Send a copy to')}</UX.SectionLabel>
        {/* Per-cohort items support CC too: static emails bundle to one copy,
            role recipients fan out per participant (bead new-main-v7x7). */}
        <>
            {selectedCCRoles.length > 0 && (
              <div style={ccChipRowStyle}>
                {selectedCCRoles.map((r, i) => {
                  const descriptor = actorDescriptors[r.key];
                  const chipColor = descriptor?.color || UX.tokens.color.n300;
                  const isLegacyColor =
                    !!descriptor &&
                    !ACTOR_COLORS_LIGHT.some(
                      (c) => c.hex.toLowerCase() === chipColor.toLowerCase()
                    );
                  return (
                    <UX.ActorChip
                      key={`ccr-${i}`}
                      color={chipColor}
                      dimmed={isLegacyColor}
                      onRemove={() =>
                        onChangeReceivesCopyRoles(
                          ccRolesForChange.filter((_, idx) => idx !== i)
                        )
                      }
                      removeAriaLabel={t('Remove')}
                    >
                      {ccRoleLabel(r)}
                    </UX.ActorChip>
                  );
                })}
              </div>
            )}
            {selectedCCEmails.length > 0 && (
              <div style={ccChipRowStyle}>
                {selectedCCEmails.map((e, i) => (
                  <UX.ActorChip
                    key={`cce-${i}`}
                    color={UX.tokens.color.n200}
                    onRemove={() =>
                      onChangeReceivesCopyEmails(
                        selectedCCEmails.filter((_, idx) => idx !== i)
                      )
                    }
                    removeAriaLabel={t('Remove')}
                  >
                    {e}
                  </UX.ActorChip>
                ))}
              </div>
            )}
            {ccMode === 'role' ? (
              <ClassicBlue600 style={{ display: 'flex', gap: 6 }}>
                <TextField
                  select
                  variant="outlined"
                  style={{ flex: 1 }}
                  value={ccRoleDraft}
                  onChange={(ev) => setCcRoleDraft(ev.target.value)}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (v) => {
                      const selected = availableCCRoles.find(
                        (o) => ccRoleOptionValue(o) === v
                      );
                      return selected ? (
                        ccRoleLabel(selected)
                      ) : (
                        <span style={{ color: UX.tokens.color.grey700 }}>
                          {t('Pick a role…')}
                        </span>
                      );
                    }
                  }}
                >
                  {availableCCRoles.length === 0 ? (
                    <MenuItem value="" disabled>
                      {hasSupportingActors
                        ? // at least one eligible (non-archived, non-channel) actor
                          // exists, but all are already CC'd
                          t('No more actors to select')
                        : // no eligible actor ever existed to begin with
                          t('No eligible supporting actors')}
                    </MenuItem>
                  ) : (
                    <MenuItem value="" disabled></MenuItem>
                  )}
                  {availableCCRoles.map((o) => (
                    <MenuItem key={ccRoleOptionValue(o)} value={ccRoleOptionValue(o)}>
                      {ccRoleLabel(o)}
                    </MenuItem>
                  ))}
                </TextField>
                <Button variant="contained" color="primary" onClick={addCcRole}>
                  {t('Add')}
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    setCcMode(null);
                    setCcRoleDraft('');
                  }}
                >
                  {t('Cancel')}
                </Button>
              </ClassicBlue600>
            ) : ccMode === 'person' ? (
              <ClassicBlue600 style={{ display: 'flex', gap: 6 }}>
                <TextField
                  type="email"
                  placeholder="name@company.com"
                  variant="outlined"
                  style={{ flex: 1 }}
                  value={ccEmailDraft}
                  onChange={(ev) => setCcEmailDraft(ev.target.value)}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') addCcEmail();
                  }}
                />
                <Button variant="contained" color="primary" onClick={addCcEmail}>
                  {t('Add')}
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    setCcMode(null);
                    setCcEmailDraft('');
                  }}
                >
                  {t('Cancel')}
                </Button>
              </ClassicBlue600>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <UX.RingIconButton
                  type="button"
                  onClick={() => setCcMode('role')}
                >
                  <UserPlus size={15} strokeWidth={1.5} />
                  {t('Supporting actor')}
                </UX.RingIconButton>
                <UX.RingIconButton
                  type="button"
                  onClick={() => setCcMode('person')}
                >
                  <MailPlus size={15} strokeWidth={1.5} />
                  {t('Email address')}
                </UX.RingIconButton>
              </div>
            )}
          </>
      </>
    );

    const channelsSection = (
      <>
        <UX.SectionLabel style={{ marginTop: 0 }}>
          {t('Channels')}
        </UX.SectionLabel>
        <UX.ChipRow>
          {selectableChannels.map((c) => {
            const key = channelToKey(c.channel);
            const str = channelToString(c.channel, t);
            if (!key || !str) return null;
            return (
              <UX.ChannelChip
                key={key}
                icon={channelIcon(c.channel)}
                label={str}
                selected={channelKeys.has(key)}
                disabled={c.disabled}
                onToggle={() => toggleChannel(c)}
              />
            );
          })}
        </UX.ChipRow>
      </>
    );

    const perCohortDisabled = hasSMSChannel;
    const batchSection =
      SHOW_PER_COHORT_BATCHING && actor !== PARTICIPANT_ACTOR_KEY ? (
        <>
          <UX.SectionLabel>
            {type === 'MSG' ? t('Send this message') : t('Send this task')}
          </UX.SectionLabel>
          <UX.RadioCard
            selected={batchMode === BatchMode.DEFAULT}
            title={t('Once Per Participant')}
            description={t(
              'Messages and tasks relating to the same participant and starting at the same time will be sent together in one notification.'
            )}
            onSelect={() => onBatchModeChange(BatchMode.DEFAULT)}
          />
          <UX.RadioCard
            selected={batchMode === BatchMode.PER_COHORT}
            disabled={perCohortDisabled}
            title={t('Once Per Cohort')}
            description={
              hasSMSChannel
                ? t('This option is not available for SMS')
                : t(
                    '{{ type }} relating to participants in the same cohort will be aggregated and sent together in one notification.',
                    { type: type === 'MSG' ? t('Messages') : t('Tasks') }
                  )
            }
            onSelect={() => onBatchModeChange(BatchMode.PER_COHORT)}
          />
        </>
      ) : null;

    const senderSection = !selectedActorOptionIsSlackChannel ? (
      <>
        <UX.SectionLabel>{t('Sender')}</UX.SectionLabel>
        <div style={{ marginBottom: 10 }}>
          <CustomSenderTextField
            newUx
            label={t('Sender Name')}
            value={value.customSender?.name || ''}
            defaultValue={expTpl.notificationSender.name || ''}
            onChange={onChangeCustomSenderName}
            tokens={nameTokens}
          />
        </div>
        <CustomSenderTextField
          newUx
          label={t('Reply-to Email')}
          value={value.customSender?.replyTo || ''}
          defaultValue={expTpl.notificationSender.replyTo || ''}
          onChange={onChangeCustomSenderReplyTo}
          tokens={emailTokens}
        />
      </>
    ) : null;

    if (newUxSection === 'audience') {
      return (
        <div>
          {recipientSection}
          {ccSection}
        </div>
      );
    }
    return (
      <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
        <UX.PanelHeader
          icon={<SlidersHorizontal size={17} />}
          title={t('Delivery')}
          hint={t('How and from where this is delivered.')}
        />
        {channelsSection}
        {batchSection}
        {senderSection}
      </div>
    );
  }

  return (
    <SideContainer>
      <SideContainerHeader
        def={{
          label: title,
          description,
          icon: ICON
        }}
        onBack={onBack}
      />
      <SettingsSection>
        <Label label={t('Recipient')} />
        <RadioPanel
          value={actor}
          onChange={(nextActor) => {
            console.log('SA', expTpl.supportingActors);
            const supportingActor = expTpl.supportingActors[nextActor] as
              | ISupportingActor
              | undefined; // Wil return undefined for the PARTICIPANT
            const slackChannel: ScheduleChannel = {
              type: ScheduleChannelType.SLACK
            };
            const nextChannels =
              supportingActor?.type === SupportingActorType.CHANNEL &&
              supportingActor?.channeltype === ChannelType.SLACK
                ? [slackChannel]
                : CHANNELS.filter(
                    (c) =>
                      c.type === ScheduleChannelType.EMAIL &&
                      c.emailFrom === 'email'
                  );

            onChangeChannels(nextChannels);

            onChangeActor(nextActor);
          }}
          orientation="vertical"
          variant="dense"
          options={actorOptions.map((o) => {
            if (o.value === actor) {
              const selectableChannels = getSelectableChannels(
                actor,
                activeIntegrations,
                smsEnabled,
                batchMode,
                o.isSlackChannel,
                t
              );

              const keys = new Set(channels.map(channelToKey));

              return {
                ...o,
                body: (
                  <FormGroup
                    classes={{
                      root: css({
                        '&&': {
                          marginLeft: theme.spacing(3),
                          marginTop: theme.spacing(2)
                        }
                      })
                    }}
                  >
                    {selectableChannels.map((c) => {
                      const key = channelToKey(c.channel);
                      const str = channelToString(c.channel, t);
                      if (!key || !str) {
                        return null;
                      }
                      return (
                        <Tooltip
                          title={c.tooltip || ''}
                          key={key}
                          placement="right"
                        >
                          <FormControlLabel
                            label={str}
                            control={
                              <Checkbox
                                color="primary"
                                checked={keys.has(key)}
                                disabled={c.disabled}
                                onChange={(ev) => {
                                  const { checked } = ev.target;
                                  if (checked) {
                                    onChangeChannels(
                                      uniqBy(
                                        sortBy(
                                          [...channels, c.channel],
                                          channelToKey
                                        ),
                                        channelToKey
                                      )
                                    );
                                  } else {
                                    const i = channels.findIndex(
                                      (c) => channelToKey(c) === key
                                    );
                                    if (i !== -1) {
                                      const nextValue = channels.slice();
                                      nextValue.splice(i, 1);
                                      onChangeChannels(nextValue);
                                    }
                                  }
                                }}
                              />
                            }
                          />
                        </Tooltip>
                      );
                    })}
                  </FormGroup>
                )
              };
            }
            return o;
          })}
        />
      </SettingsSection>
      {SHOW_PER_COHORT_BATCHING && actor !== PARTICIPANT_ACTOR_KEY && (
        <SettingsSection>
          <Label
            label={
              type === 'MSG' ? t('Send this message') : t('Send this task')
            }
          />
          <RadioPanel
            variant="compact"
            orientation="vertical"
            value={batchMode}
            onChange={(nextBatchMode) => {
              onBatchModeChange(nextBatchMode);
            }}
            options={[
              {
                label: 'Once Per Participant',
                value: BatchMode.DEFAULT,
                description: t(
                  'Messages and tasks relating to the same participant and starting at the same time will be sent together in one notification.'
                )
              },
              {
                label: 'Once Per Cohort',
                value: BatchMode.PER_COHORT,
                description: t(
                  '{{ type }} relating to participants in the same cohort will be aggregated and sent together in one notification.',
                  { type: type === 'MSG' ? t('Messages') : t('Tasks') }
                ),
                disabled: hasSMSChannel,
                tooltip: hasSMSChannel
                  ? t('This option is not available for SMS')
                  : undefined
              }
            ]}
          />
        </SettingsSection>
      )}
      <SettingsSection>
        <Label label={t('Receives a copy')} />
          {/* Per-cohort items support CC too: static emails bundle to one copy,
              role recipients fan out per participant (bead new-main-v7x7). */}
          <>
            <FormRow>
              <ActionActorSelectorMulti
                label={t('Supporting Actors')}
                fullWidth
                value={selectedCCRoles}
                onChange={(nextActors: ActionActor[]) =>
                  onChangeReceivesCopyRoles(
                    nextActors
                      .filter((a): a is Extract<ActionActor, { type: 'actor' }> => a.type === 'actor')
                      .map((a) => ({ key: a.key, emailField: a.emailField }))
                  )
                }
                expTpl={expTpl}
                filterOptions={(options: ActionActor[]) =>
                  options.filter(
                    (o) =>
                      o.type === 'actor' &&
                      o.key !== actor &&
                      !selectedCCRoles.find((s) => s.key === o.key && s.emailField === (o as Extract<ActionActor, { type: 'actor' }>).emailField)
                  )
                }
              />
            </FormRow>
            <FormRow>
              <AutocompleteMultiFreeSolo
                label={t('Email Addresses')}
                fullWidth
                value={selectedCCEmails}
                onChange={onChangeReceivesCopyEmails}
                options={[]}
              />
            </FormRow>
            <Typography variant="caption" color="textSecondary">
              {t(
                'A non-interactive copy of the {{ type }} will be sent to these people.',
                { type: type === 'TASK' ? t('task') : t('message') }
              )}
            </Typography>
          </>
      </SettingsSection>
      {!selectedActorOptionIsSlackChannel && (
        <SettingsSection>
          <Label
            label={t('Sender')}
            info={t(
              `Will be shown as the sender's name in emails. The email address and Slack user cannot be customized.`
            )}
          />
          <FormRow>
            <CustomSenderTextField
              label={t('Sender Name')}
              value={value.customSender?.name || ''}
              defaultValue={expTpl.notificationSender.name || ''}
              onChange={onChangeCustomSenderName}
              tokens={nameTokens}
            />
          </FormRow>

          <FormRow>
            <CustomSenderTextField
              label={t('Reply-to Email')}
              value={value.customSender?.replyTo || ''}
              defaultValue={expTpl.notificationSender.replyTo || ''}
              onChange={onChangeCustomSenderReplyTo}
              tokens={emailTokens}
            />
          </FormRow>
        </SettingsSection>
      )}
    </SideContainer>
  );
};